import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * EU-718 — a etapa Formação lista **grupos de curso** (`core.course_group`),
 * não cursos individuais, e a busca é filtrada no BACKEND.
 */
export interface CoursesByAreaResponse {
  areas: {
    id: number;
    name: string;
    courseGroups: { id: number; name: string; courseTypeName: string }[];
  }[];
}

export class CoursesByAreaFetchError extends Error {
  readonly name = 'CoursesByAreaFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Map the job's `graduationTypes` tokens to `core.course_type.id`s used by
 * `GET /jobs/courses-by-area`. Mirrors the table in
 * `core/backend/src/modules/job/domain/job.types.ts` —
 * `courseTypeIdForGraduation`. Kept inline here so the SPA doesn't reach into
 * core types; the price is pinning the mapping in two places, but the seed
 * (`seeds/education/course_type.json`) is stable and any drift breaks the
 * BFF + Core test pair before it reaches the user.
 *
 * EU-718: continua valendo com grupos — `core.course_group.course_type_id` é
 * NOT NULL, então o grupo herda exatamente o tipo do curso.
 */
const COURSE_TYPE_ID_BY_GRADUATION: Record<string, number> = {
  'ensino-fundamental': 1,
  'ensino-medio': 2,
  tecnico: 3,
  tecnologo: 4,
  licenciatura: 5,
  bacharelado: 6,
  doutorado: 7,
  mestrado: 8,
  'pos-graduacao': 9,
};

/**
 * EU-768 — tipos escolares (Ensino fundamental / Ensino médio) não têm grupo
 * de curso: não existe seleção de cursos para eles. Ficam de fora do filtro
 * para que a etapa Formação não dispare `courses-by-area` à toa e para que o
 * auto-prune trate "só escolar" como "nenhum tipo com catálogo".
 */
const SCHOOL_GRADUATION_TYPES: ReadonlySet<string> = new Set([
  'ensino-fundamental',
  'ensino-medio',
]);

export function courseTypeIdsForGraduation(graduationTypes: readonly string[]): number[] {
  const ids = graduationTypes
    .filter((token) => !SCHOOL_GRADUATION_TYPES.has(token))
    .map((token) => COURSE_TYPE_ID_BY_GRADUATION[token])
    .filter((id): id is number => typeof id === 'number');
  return Array.from(new Set(ids)).sort((a, b) => a - b);
}

/**
 * `query` entra na chave: a lista sem busca é uma entrada de cache própria, o
 * que faz "limpar a busca" voltar instantâneo (sem round-trip) e mantém
 * `totalAreasCount` disponível para o rodapé.
 */
export function coursesByAreaQueryKey(courseTypeIds: readonly number[], query = '') {
  return ['recruiter', 'jobs', 'courses-by-area', [...courseTypeIds], query] as const;
}

async function fetchCoursesByArea(
  courseTypeIds: readonly number[],
  query: string,
  signal?: AbortSignal
): Promise<CoursesByAreaResponse> {
  // Repeated query params: `?courseTypeId=6&courseTypeId=4`. URLSearchParams
  // appends, so we get the right shape for the BFF Zod union schema.
  const params = new URLSearchParams();
  for (const id of courseTypeIds) {
    params.append('courseTypeId', String(id));
  }
  // Só manda `query` quando há busca — o BFF normaliza vazio para undefined,
  // mas evitar o parâmetro mantém a URL (e o cache HTTP) limpos.
  const trimmed = query.trim();
  if (trimmed) params.append('query', trimmed);

  const res = await fetchClient<CoursesByAreaResponse>({
    method: 'GET',
    url: `/jobs/courses-by-area?${params.toString()}`,
    signal,
  });
  if (res.error) {
    throw new CoursesByAreaFetchError(
      `Failed to load course groups by area (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

export function coursesByAreaQueryOptions(courseTypeIds: readonly number[], query = '') {
  // When no graduation type maps to a course_type, the query is disabled —
  // the picker has no meaningful filter. Emit a fixed empty envelope rather
  // than firing a 400.
  const enabled = courseTypeIds.length > 0;
  return queryOptions({
    queryKey: coursesByAreaQueryKey(courseTypeIds, query.trim()),
    queryFn: ({ signal }) => fetchCoursesByArea(courseTypeIds, query, signal),
    enabled,
    // Course-group catalogue is effectively static within a session — avoid
    // refetch thrash when the picker re-renders.
    staleTime: 5 * 60_000,
    // EU-718 — a busca virou server-side. Sem isto a lista piscaria vazia a
    // cada tecla (nova queryKey = novo cache miss = `data: undefined`), o que
    // regrediria os estados da etapa (FRM-015). Com `keepPreviousData` a
    // listagem anterior fica na tela até a nova resposta chegar.
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      if (err instanceof CoursesByAreaFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
